/* PPE Parts & Supply: global scripts. No dependencies.
   Custom elements: product-form, product-info, quantity-input, cart-toast, menu-drawer,
   predictive-search, slide-show, tabs-component, countdown-timer, saved-products.
   Events dispatched on document: cart:updated {cart}, saved:changed {key}. */
(function () {
  'use strict';

  const theme = (window.theme = window.theme || {});
  const routes = theme.routes || {};
  const strings = theme.strings || {};

  const debounce = (fn, wait = 250) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(null, args), wait);
    };
  };
  theme.debounce = debounce;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  theme.parseHTML = parseHTML;

  const rootUrl = () => {
    const root = routes.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  theme.formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat(theme.locale, { style: 'currency', currency: theme.currency }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  };

  let liveRegion;
  theme.announce = (message) => {
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    window.setTimeout(() => { liveRegion.textContent = message; }, 50);
  };

  /* ------------------------------------------------------------------
     Cart state
     ------------------------------------------------------------------ */
  theme.cart = {
    async get() {
      const response = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } });
      return response.json();
    },
    render(cart) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
      document.querySelectorAll('[data-cart-total]').forEach((el) => {
        el.textContent = `${theme.formatMoney(cart.total_price)} ${cart.currency}`;
      });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    },
    async refresh() {
      const cart = await this.get();
      this.render(cart);
      return cart;
    }
  };

  /* ------------------------------------------------------------------
     <product-form>: AJAX add to cart
     ------------------------------------------------------------------ */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form || this.dataset.bound) return;
      this.dataset.bound = 'true';
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    async onSubmit(event) {
      event.preventDefault();
      const button = event.submitter || this.form.querySelector('[type="submit"]');
      if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;

      const error = this.querySelector('[data-form-error]');
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }

      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');

      try {
        const response = await fetch(routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(this.form)
        });
        const data = await response.json();

        if (data.status) {
          if (error) {
            error.textContent = data.description || data.message || strings.cartError;
            error.hidden = false;
          }
          return;
        }

        await theme.cart.refresh();
        this.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: data }));

        // Close the quick view first: an open modal dialog would sit above the drawer.
        const dialog = this.closest('dialog');
        if (dialog && dialog.open) dialog.close();

        if (!this.hasAttribute('data-no-toast')) {
          const drawer = document.querySelector('cart-drawer');
          const toast = document.querySelector('cart-toast');
          if (drawer && !drawer.disabled && typeof drawer.openAfterAdd === 'function') drawer.openAfterAdd();
          else if (toast) toast.show(data.items ? data.items[0] : data);
        }
      } catch (e) {
        if (error) {
          error.textContent = strings.cartError;
          error.hidden = false;
        }
      } finally {
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ------------------------------------------------------------------
     <product-info>: variant changes, quantity-driven totals, section sync
     Contract:
       data-section-id, data-product-url, data-update-url="true|false"
       <script type="application/json" data-product-json> product.variants </script>
       select[data-variant-select] | input[data-variant-radio]
       [data-sku] [data-add-button] [data-add-label]
       [data-variant-sync][id]: replaced from the re-rendered section on change
     ------------------------------------------------------------------ */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      try {
        this.variants = json ? JSON.parse(json.textContent) : [];
      } catch (e) {
        this.variants = [];
      }
      const idInput = this.querySelector('[data-variant-select], input[data-variant-radio]:checked, input[name="id"]');
      this.current = this.variants.find((v) => String(v.id) === String(idInput && idInput.value)) || this.variants[0];

      this.addEventListener('change', (event) => {
        const target = event.target;
        if (target.matches('[data-variant-select], input[data-variant-radio]')) {
          this.onVariantChange(target.value);
        }
      });
    }

    get quantity() {
      const input = this.querySelector('input[name="quantity"]');
      const value = parseInt(input ? input.value : '1', 10);
      return Number.isNaN(value) || value < 1 ? 1 : value;
    }

    onVariantChange(id) {
      const variant = this.variants.find((v) => String(v.id) === String(id));
      if (!variant) return;
      this.current = variant;

      this.querySelectorAll('input[type="hidden"][name="id"]').forEach((input) => { input.value = variant.id; });
      this.querySelectorAll('[data-sku]').forEach((el) => { el.textContent = variant.sku || 'N/A'; });

      const button = this.querySelector('[data-add-button]');
      if (button) {
        button.disabled = !variant.available;
        const label = button.querySelector('[data-add-label]');
        if (label) label.textContent = variant.available ? strings.addToCart : strings.soldOut;
      }

      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
      this.syncSections(variant.id);
    }

    async syncSections(variantId) {
      const sectionId = this.dataset.sectionId;
      const productUrl = this.dataset.productUrl;
      if (!sectionId || !productUrl) return;
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      try {
        const separator = productUrl.includes('?') ? '&' : '?';
        const response = await fetch(`${productUrl}${separator}variant=${variantId}&section_id=${sectionId}`, {
          signal: this.abortController.signal
        });
        const doc = parseHTML(await response.text());
        this.querySelectorAll('[data-variant-sync]').forEach((el) => {
          const fresh = doc.getElementById(el.id);
          if (fresh) el.innerHTML = fresh.innerHTML;
        });
      } catch (e) {
        /* aborted or offline, keep current markup */
      }
    }
  }
  customElements.define('product-info', ProductInfo);

  /* ------------------------------------------------------------------
     <quantity-input>
     Contract: button[name="minus"], input[type=number], button[name="plus"]
     ------------------------------------------------------------------ */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input || this.dataset.bound) return;
      this.dataset.bound = 'true';
      this.querySelectorAll('button[name="minus"], button[name="plus"]').forEach((button) => {
        button.addEventListener('click', (event) => this.step(event));
      });
    }

    step(event) {
      event.preventDefault();
      const previous = this.input.value;
      if (event.currentTarget.name === 'plus') this.input.stepUp();
      else this.input.stepDown();
      if (previous !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------------
     <cart-toast>
     ------------------------------------------------------------------ */
  class CartToast extends HTMLElement {
    connectedCallback() {
      const close = this.querySelector('[data-toast-close]');
      if (close) close.addEventListener('click', () => this.hide());
      this.addEventListener('mouseenter', () => clearTimeout(this.timer));
      this.addEventListener('mouseleave', () => this.schedule());
      this.addEventListener('focusin', () => clearTimeout(this.timer));
    }

    show(item) {
      const title = this.querySelector('[data-toast-title]');
      const media = this.querySelector('[data-toast-image]');
      if (title) title.textContent = item && item.product_title ? `${item.quantity} × ${item.product_title}` : '';
      if (media) {
        media.innerHTML = '';
        const src = item && ((item.featured_image && item.featured_image.url) || item.image);
        if (src) {
          const img = document.createElement('img');
          img.src = `${src}${src.includes('?') ? '&' : '?'}width=120`;
          img.alt = '';
          img.width = 56;
          img.height = 56;
          media.appendChild(img);
        }
      }
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-visible'));
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.hide(), 6000);
    }

    hide() {
      this.classList.remove('is-visible');
      setTimeout(() => { this.hidden = true; }, 220);
    }
  }
  customElements.define('cart-toast', CartToast);

  /* ------------------------------------------------------------------
     <menu-drawer id="…">: opened by any [aria-controls="<id>"]
     ------------------------------------------------------------------ */
  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.toggles = Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
      this.toggles.forEach((toggle) => toggle.addEventListener('click', () => this.open(toggle)));
      this.querySelectorAll('[data-drawer-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab') this.trapFocus(event);
      });
    }

    open(trigger) {
      this.trigger = trigger;
      this.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      this.toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'true'));
      const close = this.querySelector('.drawer__close');
      if (close) close.focus();
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      this.toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
      if (this.trigger) this.trigger.focus();
    }

    trapFocus(event) {
      const focusable = Array.from(
        this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, summary, [tabindex="0"]')
      ).filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }
  customElements.define('menu-drawer', MenuDrawer);

  /* Disclosures (<details data-disclosure>) close on outside click / Escape */
  document.addEventListener('click', (event) => {
    document.querySelectorAll('details[data-disclosure][open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('details[data-disclosure][open]').forEach((details) => {
      details.removeAttribute('open');
      const summary = details.querySelector('summary');
      if (summary) summary.focus();
    });
  });

  /* Country filter. Markets can list ~200 countries, so the panel filters as the
     shopper types. Enter must never reach the form, or it submits whichever
     country happens to be first and navigates away. */
  document.querySelectorAll('[data-country-filter]').forEach((panel) => {
    const list = panel.querySelector('[data-country-list]');
    const details = panel.closest('details');
    /* The countries ship once per page in #CountryListTemplate so they cost nothing
       until a panel opens; each panel takes its own copy. */
    const fill = () => {
      const template = document.getElementById('CountryListTemplate');
      if (!list || !template || list.childElementCount) return;
      list.appendChild(template.content.cloneNode(true));
    };
    if (details) {
      details.addEventListener('toggle', fill);
      details.querySelector('summary').addEventListener('pointerenter', fill, { once: true });
    }

    const input = panel.querySelector('[data-country-search]');
    if (!input) return;
    let items = [];
    const empty = panel.querySelector('[data-country-empty]');
    const status = panel.querySelector('[data-country-status]');

    const apply = () => {
      fill();
      items = Array.from(panel.querySelectorAll('[data-country-item]'));
      const term = input.value.trim().toLowerCase();
      let shown = 0;
      items.forEach((item) => {
        const match = !term || (item.dataset.search || '').indexOf(term) > -1;
        item.hidden = !match;
        if (match) shown += 1;
      });
      if (empty) empty.hidden = shown > 0;
      if (list) list.hidden = shown === 0;
      if (status) status.textContent = shown === 0 ? '' : shown + ' of ' + items.length;
      return shown;
    };

    input.addEventListener('input', apply);

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        const first = items.find((item) => !item.hidden);
        const button = first && first.querySelector('button');
        if (button) button.click();
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const first = items.find((item) => !item.hidden);
        if (first) first.querySelector('button').focus();
      }
    });

    /* Reset and focus the box each time the panel opens. */
    if (details) {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        input.value = '';
        apply();
        input.focus();
      });
    }
  });

  /* Mega menu parents: the dropdown itself is opened by CSS (:hover / :focus-within), so the
     link only has to report the state. Escape hides it in place, without asking the shopper to
     move the pointer (WCAG 1.4.13); hovering or focusing the item again brings it back. */
  const navParent = (node) => (node instanceof Element ? node.closest('.main-nav__item--parent') : null);

  const setNavExpanded = (item, open) => {
    const link = item.querySelector('.main-nav__link');
    const dropdown = item.querySelector('.main-nav__dropdown');
    if (!link || !dropdown) return;
    if (open) dropdown.hidden = false;
    link.setAttribute('aria-expanded', String(open));
  };

  ['mouseover', 'focusin'].forEach((type) => {
    document.addEventListener(type, (event) => {
      const item = navParent(event.target);
      if (item && navParent(event.relatedTarget) !== item) setNavExpanded(item, true);
    });
  });

  ['mouseout', 'focusout'].forEach((type) => {
    document.addEventListener(type, (event) => {
      const item = navParent(event.target);
      if (!item || item.contains(event.relatedTarget)) return;
      /* Pointer and keyboard can hold the same dropdown open; only the last one out closes it. */
      if (type === 'mouseout' && item.contains(document.activeElement)) return;
      if (type === 'focusout' && item.matches(':hover')) return;
      setNavExpanded(item, false);
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const item = navParent(event.target);
    if (!item) return;
    const dropdown = item.querySelector('.main-nav__dropdown');
    const link = item.querySelector('.main-nav__link');
    if (dropdown) dropdown.hidden = true;
    if (link) {
      link.setAttribute('aria-expanded', 'false');
      link.focus();
    }
  });

  /* Search forms: drop empty optional selects (e.g. "All Categories") so URLs stay clean */
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-search-form]');
    if (!form) return;
    /* A category chosen with nothing typed is a request to browse it. The
       search page needs a term to show anything, so go to the category's
       product listing instead. */
    const typeSelect = form.querySelector('select[name="filter.p.product_type"]');
    const termInput = form.querySelector('input[name="q"]');
    if (typeSelect && typeSelect.value && termInput && !termInput.value.trim()) {
      event.preventDefault();
      const base = (routes.collections || '/collections').replace(/\/$/, '');
      window.location.href = `${base}/types?q=${encodeURIComponent(typeSelect.value)}`;
      return;
    }
    form.querySelectorAll('select[data-optional]').forEach((select) => {
      if (!select.value) select.disabled = true;
    });
  });
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('select[data-optional]').forEach((select) => { select.disabled = false; });
  });

  /* ------------------------------------------------------------------
     Part numbers: "246-065-BG01" and "246065BG01" should find the same
     part. Shopify search splits on dashes, so a query that finds nothing
     is retried in its compact form, which matches the compact part-number
     tags on each product. Returns null when the query is not a part number.
     ------------------------------------------------------------------ */
  const compactPartNumber = (query) => {
    const q = (query || '').trim();
    if (q.length > 40 || !/\d/.test(q) || !/[-./]/.test(q)) return null;
    if (!/^[A-Za-z0-9]+(?:[-./\s]+[A-Za-z0-9]+)+$/.test(q)) return null;
    return q.replace(/[-./\s]+/g, '').toUpperCase();
  };
  theme.compactPartNumber = compactPartNumber;

  /* In a SKU the letter O and the digit 0 look the same, so "BASO30106038" is
     often typed for "BAS030106038". Returns the query with every O that touches
     a digit turned into 0, or null when there is nothing to change. */
  const zeroForO = (query) => {
    const q = (query || '').trim();
    if (!q || q.length > 40 || /\s/.test(q) || !/\d/.test(q) || !/o/i.test(q)) return null;
    const chars = q.split('');
    let changed = true;
    while (changed) {
      changed = false;
      chars.forEach((char, i) => {
        if (!/o/i.test(char)) return;
        if (/\d/.test(chars[i - 1] || '') || /\d/.test(chars[i + 1] || '')) {
          chars[i] = '0';
          changed = true;
        }
      });
    }
    const swapped = chars.join('');
    return swapped === q ? null : swapped;
  };
  /* Heidelberg numbers are written 66.020.124, 66-020-124, 66020124 or with our
     HE- prefix, and the catalog holds both the dotted and the HE-dashed form.
     Returns those two spellings for a query that has that shape, else []. */
  const heidelbergForms = (query) => {
    const q = (query || '').trim().toUpperCase().replace(/^HE[-.\s]?(?=[A-Z0-9]{2}[-.\s]?\d{3})/, '');
    const m = /^([A-Z0-9]{2})[-.\s]?(\d{3})[-.\s]?(\d{3,4})([A-Z])?(?:\s?\/\s?(\d{2}))?$/.exec(q);
    if (!m) return [];
    const tail = (m[4] || '') + (m[5] ? `/${m[5]}` : '');
    return [`${m[1]}.${m[2]}.${m[3]}${tail}`, `HE-${m[1]}-${m[2]}-${m[3]}${tail}`];
  };
  /* Ryobi numbers are written 5290-33-314 (sometimes with a -1 suffix) and AB Dick
     numbers P-36262; typed without the dashes, Shopify finds neither. Returns the
     dashed spelling for a run of digits in that shape, else []. */
  const dashedForms = (query) => {
    const q = (query || '').trim().toUpperCase();
    const ryobi = /^(\d{4})(\d{2})(\d{3})(\d)?$/.exec(q);
    if (ryobi) return [`${ryobi[1]}-${ryobi[2]}-${ryobi[3]}${ryobi[4] ? `-${ryobi[4]}` : ''}`];
    const abDick = /^P(\d{4,6}[A-Z]?)$/.exec(q);
    if (abDick) return [`P-${abDick[1]}`];
    return [];
  };
  /* Other spellings of a part number to try when a search finds nothing. */
  const partNumberAlternates = (query) => {
    const compact = compactPartNumber(query);
    const typed = (query || '').trim().toUpperCase();
    return [...heidelbergForms(query), ...dashedForms(query), compact, zeroForO(query), zeroForO(compact)]
      .filter((value, i, all) => value && value !== typed && all.indexOf(value) === i);
  };
  theme.partNumberAlternates = partNumberAlternates;
  /* Every spelling worth searching for what was typed, best first. A Heidelberg
     number typed with spaces, dashes or the HE- prefix leads with the dotted form,
     because "91 161 1321" otherwise matches unrelated parts word by word. */
  const searchQueries = (query) => {
    const typed = (query || '').trim();
    const [dotted] = heidelbergForms(typed);
    const lead = dotted && /^HE|[-.\s]/i.test(typed) ? dotted : typed;
    const seen = new Set();
    return [lead, typed, ...partNumberAlternates(typed)].filter((value) => {
      const key = (value || '').toUpperCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  theme.searchQueries = searchQueries;
  /* Send the search page the leading spelling, keeping what was typed in pq so
     the zero-results retry can still fall back to it. */
  const leadSearch = (params) => {
    const typed = (params.get('q') || '').trim();
    const [lead] = searchQueries(typed);
    if (params.get('pq') || !lead || lead.toUpperCase() === typed.toUpperCase()) return false;
    params.set('pq', typed);
    params.set('q', lead);
    return true;
  };
  document.addEventListener('submit', (event) => {
    const form = event.target;
    const field = form.matches('form[role="search"]') && form.elements.q;
    if (!field) return;
    form.querySelectorAll('input[data-typed-query]').forEach((input) => input.remove());
    const params = new URLSearchParams({ q: field.value });
    if (!leadSearch(params)) return;
    const original = document.createElement('input');
    original.type = 'hidden';
    original.name = 'pq';
    original.dataset.typedQuery = '';
    original.value = params.get('pq');
    form.appendChild(original);
    field.value = params.get('q');
  });
  if (routes.search && window.location.pathname === routes.search) {
    const url = new URL(window.location.href);
    if (leadSearch(url.searchParams)) window.location.replace(url.toString());
  }

  /* ------------------------------------------------------------------
     <predictive-search>: renders sections/predictive-search.liquid
     ------------------------------------------------------------------ */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.status = this.querySelector('[data-predictive-status]');
      this.picks = this.querySelector('[data-press-picks]');
      if (!this.input || !this.results || !routes.predictiveSearch) return;

      this.input.addEventListener('input', debounce(() => this.onInput(), 260));
      this.input.addEventListener('input', () => this.togglePicks());
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim().length >= 2 && this.results.innerHTML.trim()) this.open();
        this.togglePicks();
      });
      this.addEventListener('focusout', () => {
        setTimeout(() => {
          if (!this.contains(document.activeElement)) {
            this.close();
            this.hidePicks();
          }
        }, 0);
      });
      /* The category select shares this element, so leaving the input for it
         doesn't fire focusout: close the panels ourselves so they don't sit under its menu. */
      const leaveForSelect = (event) => {
        if (!event.target.closest('select')) return;
        this.close();
        this.hidePicks();
      };
      this.addEventListener('focusin', leaveForSelect);
      this.addEventListener('pointerdown', leaveForSelect);
      if (this.picks) {
        this.rememberedPress();
        this.picks.addEventListener('click', (event) => {
          const chip = event.target.closest('[data-press]');
          if (chip) this.pickPress(chip.dataset.press);
        });
      }
      this.addEventListener('keydown', (event) => this.onKeydown(event));
    }

    async onInput() {
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.close();
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const [lead, ...spellings] = searchQueries(query);
      const params = new URLSearchParams({
        q: lead,
        'resources[type]': 'product,collection,query',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.sku,variants.title,vendor,tag',
        section_id: 'predictive-search'
      });

      try {
        const signal = this.controller.signal;
        const response = await fetch(`${routes.predictiveSearch}?${params.toString()}`, { signal });
        if (!response.ok) {
          this.close();
          return;
        }
        let content = parseHTML(await response.text()).querySelector('[data-predictive-content]');
        for (const alternate of spellings) {
          if (content && content.querySelector('[id^="ps-product-"]')) break;
          params.set('q', alternate);
          const retry = await fetch(`${routes.predictiveSearch}?${params.toString()}`, { signal });
          const retried = retry.ok ? parseHTML(await retry.text()).querySelector('[data-predictive-content]') : null;
          if (retried && retried.querySelector('[id^="ps-product-"]')) content = retried;
        }
        this.results.innerHTML = content ? content.innerHTML : '';
        if (content && content.innerHTML.trim()) this.open();
        else this.close();
        /* Swapping innerHTML is silent, so the section ships the translated count with
           the markup and we post it to the live region once the results are in place. */
        this.announce(content ? content.dataset.announcement : '');
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      }
    }

    options() {
      return Array.from(this.results.querySelectorAll('[role="option"]'));
    }

    /* "Search by press" shortcuts: shown while the box is empty. */
    togglePicks() {
      if (!this.picks) return;
      if (this.input.value.trim() === '') this.picks.hidden = false;
      else this.hidePicks();
    }

    hidePicks() {
      if (this.picks) this.picks.hidden = true;
    }

    pickPress(press) {
      try { window.localStorage.setItem('ppe-press', press); } catch (error) { /* private mode */ }
      this.rememberedPress();
      this.hidePicks();
      this.input.value = `${press} `;
      this.input.focus();
      this.input.setSelectionRange(this.input.value.length, this.input.value.length);
      this.onInput();
    }

    /* The last press picked goes first and is marked, so a returning buyer is one tap away. */
    rememberedPress() {
      let saved = '';
      try { saved = window.localStorage.getItem('ppe-press') || ''; } catch (error) { return; }
      if (!saved) return;
      const list = this.picks.querySelector('.search-presses__list');
      let chip = Array.from(list.querySelectorAll('[data-press]')).find((el) => el.dataset.press === saved);
      if (!chip) {
        chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'search-presses__chip';
        chip.dataset.press = saved;
        chip.textContent = saved;
      }
      list.querySelectorAll('.is-saved').forEach((el) => el.classList.remove('is-saved'));
      chip.classList.add('is-saved');
      list.prepend(chip);
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.picks && !this.picks.hidden) {
        this.hidePicks();
        return;
      }
      if (this.results.hidden) return;
      const options = this.options();
      const index = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!options.length) return;
        const next = event.key === 'ArrowDown'
          ? (index + 1) % options.length
          : (index - 1 + options.length) % options.length;
        options.forEach((option, i) => option.setAttribute('aria-selected', String(i === next)));
        this.input.setAttribute('aria-activedescendant', options[next].id);
        options[next].scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && index > -1) {
        event.preventDefault();
        const link = options[index].matches('a') ? options[index] : options[index].querySelector('a');
        if (link) window.location.href = link.href;
      } else if (event.key === 'Escape') {
        this.close();
        this.input.focus();
      }
    }

    open() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.announce('');
    }

    announce(message) {
      if (this.status) this.status.textContent = message || '';
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------------------
     <slide-show data-autoplay="7">
     Contract: [data-slides] track, [data-slide] items, [data-slide-dot], [data-slide-prev], [data-slide-next]
     ------------------------------------------------------------------ */
  class SlideShow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slides]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-slide-dot]'));
      this.index = 0;
      if (!this.track || this.slides.length < 2) return;

      const prev = this.querySelector('[data-slide-prev]');
      const next = this.querySelector('[data-slide-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1, true));
      if (next) next.addEventListener('click', () => this.go(this.index + 1, true));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));

      this.interval = parseInt(this.dataset.autoplay || '0', 10) * 1000;
      this.status = this.querySelector('[data-slide-status]');

      /* WCAG 2.2.2 needs a real stop control, not just hover and focus. Once the
         shopper presses it, nothing may restart the timer behind their back. */
      this.stopped = false;
      this.toggle = this.querySelector('[data-slide-toggle]');
      if (this.toggle) {
        this.toggle.addEventListener('click', () => {
          this.stopped = !this.stopped;
          if (this.stopped) this.pause();
          else this.play();
          this.syncToggle();
        });
      }

      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.play());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => {
        setTimeout(() => { if (!this.contains(document.activeElement)) this.play(); }, 0);
      });

      /* Honour Reduce Motion turned on mid-session, not only at load. */
      const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      const onMotionChange = () => { if (motionQuery.matches) this.pause(); else this.play(); };
      if (motionQuery.addEventListener) motionQuery.addEventListener('change', onMotionChange);
      else if (motionQuery.addListener) motionQuery.addListener(onMotionChange);
      document.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest && event.target.closest('[data-slide]');
        const i = this.slides.indexOf(slide);
        if (i > -1) {
          this.pause();
          this.go(i);
        }
      });

      this.go(0);
      this.play();
    }

    go(i, userInitiated) {
      const count = this.slides.length;
      this.index = (i + count) % count;
      this.track.style.transform = `translateX(-${this.index * 100}%)`;
      this.slides.forEach((slide, k) => {
        const active = k === this.index;
        slide.setAttribute('aria-hidden', String(!active));
        if (active) slide.removeAttribute('inert');
        else slide.setAttribute('inert', '');
      });
      this.dots.forEach((dot, k) => dot.setAttribute('aria-current', k === this.index ? 'true' : 'false'));
      this.announce();
      if (userInitiated && !this.matches(':hover') && !this.contains(document.activeElement)) this.play();
    }

    announce() {
      if (!this.status) return;
      const template = this.dataset.labelPosition || 'Slide CURRENT of TOTAL';
      this.status.textContent = template
        .replace('CURRENT', String(this.index + 1))
        .replace('TOTAL', String(this.slides.length));
    }

    syncToggle() {
      if (!this.toggle) return;
      const pauseIcon = this.toggle.querySelector('[data-toggle-icon-pause]');
      const playIcon = this.toggle.querySelector('[data-toggle-icon-play]');
      const label = this.stopped
        ? (this.dataset.labelPlay || 'Play slideshow')
        : (this.dataset.labelPause || 'Pause slideshow');
      this.toggle.setAttribute('aria-pressed', String(this.stopped));
      this.toggle.setAttribute('aria-label', label);
      if (pauseIcon) pauseIcon.hidden = this.stopped;
      if (playIcon) playIcon.hidden = !this.stopped;
    }

    play() {
      clearInterval(this.timer);
      if (this.stopped || !this.interval || reducedMotion()) return;
      this.timer = setInterval(() => this.go(this.index + 1), this.interval);
    }

    pause() {
      clearInterval(this.timer);
    }
  }
  customElements.define('slide-show', SlideShow);

  /* ------------------------------------------------------------------
     <tabs-component>
     Contract: [role=tab][aria-controls] buttons and [role=tabpanel][id] panels.
     ------------------------------------------------------------------ */
  class TabsComponent extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
      if (!this.tabs.length) return;
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i, false));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, i));
      });
      const initial = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      this.select(initial > -1 ? initial : 0, false);

      document.addEventListener('shopify:block:select', (event) => {
        if (!event.target.closest || !this.contains(event.target)) return;
        const tab = event.target.closest('[role="tab"]');
        const panel = event.target.closest('[role="tabpanel"]');
        const index = tab ? this.tabs.indexOf(tab) : this.panels.indexOf(panel);
        if (index > -1) this.select(index, false);
      });
    }

    select(index, focus) {
      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (this.panels[i]) this.panels[i].hidden = !selected;
      });
      if (focus) this.tabs[index].focus();
    }

    onKeydown(event, index) {
      const count = this.tabs.length;
      let next = null;
      if (event.key === 'ArrowRight') next = (index + 1) % count;
      else if (event.key === 'ArrowLeft') next = (index - 1 + count) % count;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = count - 1;
      if (next !== null) {
        event.preventDefault();
        this.select(next, true);
      }
    }
  }
  customElements.define('tabs-component', TabsComponent);

  /* ------------------------------------------------------------------
     <countdown-timer data-end="1790000000000">
     Contract: [data-hours] [data-minutes] [data-seconds]
     data-end is the end time in epoch milliseconds, worked out by the
     section so the timer matches the store's clock, not the shopper's.
     ------------------------------------------------------------------ */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.end = Number(this.dataset.end) || 0;
      this.hours = this.querySelector('[data-hours]');
      this.minutes = this.querySelector('[data-minutes]');
      this.seconds = this.querySelector('[data-seconds]');
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const left = Math.max(0, Math.floor((this.end - Date.now()) / 1000));
      const pad = (n) => String(n).padStart(2, '0');
      if (this.hours) this.hours.textContent = pad(Math.floor(left / 3600));
      if (this.minutes) this.minutes.textContent = pad(Math.floor((left % 3600) / 60));
      if (this.seconds) this.seconds.textContent = pad(left % 60);
      if (left === 0) {
        clearInterval(this.timer);
        this.dispatchEvent(new CustomEvent('countdown:end', { bubbles: true }));
      }
    }
  }
  customElements.define('countdown-timer', CountdownTimer);

  /* ------------------------------------------------------------------
     Wishlist (localStorage, handles)
     ------------------------------------------------------------------ */
  const createStore = (key, limit) => ({
    key,
    limit,
    read() {
      try {
        const list = JSON.parse(window.localStorage.getItem(key));
        return Array.isArray(list) ? list : [];
      } catch (e) {
        return [];
      }
    },
    write(list) {
      try {
        window.localStorage.setItem(key, JSON.stringify(list));
      } catch (e) {
        /* storage unavailable */
      }
      document.dispatchEvent(new CustomEvent('saved:changed', { detail: { key } }));
    },
    has(handle) {
      return this.read().includes(handle);
    },
    toggle(handle) {
      const list = this.read();
      const index = list.indexOf(handle);
      if (index > -1) {
        list.splice(index, 1);
      } else {
        if (limit && list.length >= limit) return { ok: false, reason: 'limit' };
        list.push(handle);
      }
      this.write(list);
      return { ok: true, added: index === -1 };
    },
    remove(handle) {
      this.write(this.read().filter((item) => item !== handle));
    },
    clear() {
      this.write([]);
    }
  });

  theme.wishlist = createStore('ppe:wishlist', 0);

  const syncSaved = () => {
    const wishlist = theme.wishlist.read();
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const on = wishlist.includes(button.dataset.handle);
      button.setAttribute('aria-pressed', String(on));
      if (button.hasAttribute('aria-label')) button.setAttribute('aria-label', on ? strings.wishlistRemove : strings.wishlistAdd);
    });
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = wishlist.length;
      el.hidden = wishlist.length === 0;
    });
  };
  theme.syncSaved = syncSaved;
  document.addEventListener('saved:changed', syncSaved);

  /* Re-sync toggles when sections replace markup (filters, pagination, quick view) */
  const observer = new MutationObserver(debounce(syncSaved, 60));
  observer.observe(document.body, { childList: true, subtree: true });

  /* ------------------------------------------------------------------
     Quick view: renders sections/quick-view.liquid into <dialog id="QuickView">
     ------------------------------------------------------------------ */
  const openQuickView = async (url, trigger) => {
    const dialog = document.getElementById('QuickView');
    if (!dialog || typeof dialog.showModal !== 'function') {
      window.location.href = url;
      return;
    }
    const content = dialog.querySelector('[data-quick-view-content]');
    content.innerHTML = '<div class="quick-view__loading">…</div>';
    /* The dialog opens before the fetch lands, so flag the region as busy until the
       product markup is in: without it the label is read out and then nothing. */
    content.setAttribute('aria-busy', 'true');
    dialog.showModal();
    document.body.classList.add('overflow-hidden');
    dialog.addEventListener('close', () => {
      document.body.classList.remove('overflow-hidden');
      if (trigger) trigger.focus();
    }, { once: true });

    try {
      const separator = url.includes('?') ? '&' : '?';
      const response = await fetch(`${url}${separator}section_id=quick-view`);
      const product = parseHTML(await response.text()).querySelector('[data-quick-view-product]');
      if (!product) throw new Error('missing');
      content.innerHTML = '';
      content.appendChild(document.importNode(product, true));
      content.removeAttribute('aria-busy');
      const heading = content.querySelector('h2');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus();
      }
    } catch (e) {
      window.location.href = url;
    }
  };
  theme.openQuickView = openQuickView;

  /* ------------------------------------------------------------------
     Inquiry popup: <dialog id="InquiryDialog"> from snippets/inquiry-form.liquid.
     Trigger: [data-inquiry-open] with data-product, data-sku, data-url.
     The form is a plain contact form, so the page reloads on submit and comes
     back with #InquiryForm; the popup then reopens to show the result. The
     product is kept in sessionStorage so it survives that reload.
     ------------------------------------------------------------------ */
  const INQUIRY_KEY = 'ppe:inquiry';
  const showInquiry = (dialog, item, trigger) => {
    if (item) {
      dialog.querySelector('[data-inquiry-product]').value = item.product || '';
      dialog.querySelector('[data-inquiry-sku]').value = item.sku || 'N/A';
      dialog.querySelector('[data-inquiry-url]').value = item.url || '';
    }
    dialog.showModal();
    document.body.classList.add('overflow-hidden');
    dialog.addEventListener('close', () => {
      document.body.classList.remove('overflow-hidden');
      if (trigger && trigger.isConnected) trigger.focus();
    }, { once: true });
  };

  const openInquiry = (trigger) => {
    const dialog = document.getElementById('InquiryDialog');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    const item = { product: trigger.dataset.product, sku: trigger.dataset.sku, url: trigger.dataset.url };
    try {
      sessionStorage.setItem(INQUIRY_KEY, JSON.stringify(item));
    } catch (e) {
      /* storage blocked: the popup still works, it just loses the product after an error reload */
    }
    /* Only one modal can be on top, so a quick view steps aside first. */
    const quickView = trigger.closest('dialog[open]');
    if (quickView) quickView.close();
    /* A result from an earlier inquiry would be misleading next to a new product. */
    const status = dialog.querySelector('[data-inquiry-status]');
    if (status) status.remove();
    showInquiry(dialog, item, quickView ? null : trigger);
    const first = dialog.querySelector('input[name="contact[name]"]');
    if (first) first.focus();
  };
  theme.openInquiry = openInquiry;

  const inquiryDialog = document.getElementById('InquiryDialog');
  if (inquiryDialog && typeof inquiryDialog.showModal === 'function'
    && window.location.hash === '#InquiryForm' && inquiryDialog.querySelector('[data-inquiry-status]')) {
    let item = null;
    try {
      item = JSON.parse(sessionStorage.getItem(INQUIRY_KEY));
    } catch (e) {
      item = null;
    }
    showInquiry(inquiryDialog, item, null);
  }

  document.addEventListener('click', (event) => {
    const wish = event.target.closest('[data-wishlist-toggle]');
    if (wish) {
      event.preventDefault();
      theme.wishlist.toggle(wish.dataset.handle);
      return;
    }
    const inquiry = event.target.closest('[data-inquiry-open]');
    if (inquiry) {
      event.preventDefault();
      openInquiry(inquiry);
      return;
    }
    const quick = event.target.closest('[data-quick-view]');
    if (quick) {
      event.preventDefault();
      openQuickView(quick.dataset.quickView, quick);
      return;
    }
    const dialogClose = event.target.closest('[data-dialog-close]');
    if (dialogClose) {
      const dialog = dialogClose.closest('dialog');
      if (dialog) dialog.close();
      return;
    }
    if (event.target.matches('dialog.quick-view')) event.target.close();
  });

  /* ------------------------------------------------------------------
     <saved-products data-list="wishlist" data-section="product-card-render">
     Contract: [data-saved-grid], [data-saved-empty], [data-saved-count], [data-saved-clear]
     ------------------------------------------------------------------ */
  class SavedProducts extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-saved-grid]');
      this.empty = this.querySelector('[data-saved-empty]');
      const clear = this.querySelector('[data-saved-clear]');
      if (clear) clear.addEventListener('click', () => this.store().clear());
      document.addEventListener('saved:changed', (event) => {
        if (event.detail.key === this.store().key) this.render();
      });
      this.render();
    }

    store() {
      return theme.wishlist;
    }

    async render() {
      const handles = this.store().read();
      const toolbar = this.querySelector('[data-saved-toolbar]');
      this.querySelectorAll('[data-saved-count]').forEach((el) => { el.textContent = handles.length; });

      if (!handles.length) {
        this.grid.innerHTML = '';
        this.grid.hidden = true;
        if (toolbar) toolbar.hidden = true;
        this.empty.hidden = false;
        return;
      }

      this.empty.hidden = true;
      if (toolbar) toolbar.hidden = false;
      const renderId = (this.renderId = (this.renderId || 0) + 1);

      const html = await Promise.all(handles.map(async (handle) => {
        try {
          const response = await fetch(`${rootUrl()}products/${encodeURIComponent(handle)}?section_id=${this.dataset.section}`);
          if (response.status === 404) {
            this.store().remove(handle);
            return '';
          }
          const item = parseHTML(await response.text()).querySelector('[data-saved-item]');
          return item ? item.outerHTML : '';
        } catch (e) {
          return '';
        }
      }));

      if (renderId !== this.renderId) return;
      this.grid.innerHTML = html.join('');
      this.grid.hidden = false;
    }
  }
  customElements.define('saved-products', SavedProducts);

  /* ------------------------------------------------------------------
     Contact form prefill
     Textarea [data-prefill-message] receives ?part=… and/or the cart lines when ?quote=cart.
     Inputs [data-prefill-field="name"] receive ?name=… etc.
     ------------------------------------------------------------------ */
  const prefillContact = async () => {
    const target = document.querySelector('[data-prefill-message]');
    const params = new URLSearchParams(window.location.search);
    document.querySelectorAll('[data-prefill-field]').forEach((field) => {
      const value = params.get(field.dataset.prefillField);
      if (value && !field.value) field.value = value;
    });
    if (!target || target.value.trim()) return;

    const lines = [];
    if (params.get('part')) lines.push(params.get('part'));
    if (params.get('quote') === 'cart') {
      try {
        const cart = await theme.cart.get();
        if (cart.items.length) {
          lines.push(strings.quoteIntro);
          cart.items.forEach((item) => {
            const sku = item.sku ? `${item.sku} | ` : '';
            const variant = item.variant_title ? ` (${item.variant_title})` : '';
            lines.push(`${item.quantity} × ${sku}${item.product_title}${variant}`);
          });
        }
      } catch (e) {
        /* ignore */
      }
    }
    if (lines.length) target.value = `${lines.join('\n')}\n\n`;
  };

  /* ------------------------------------------------------------------
     <review-form>
     Posts a star rating and review to Stamped, because Stamped serves its own
     form only as part of a widget it will not render for a product that has no
     reviews yet. Stays out of the way when Stamped did render its form.
     Contract: data-product-* attributes describing the product, one
     [name=reviewRating] radio group, and author/email/reviewMessage fields.
     ------------------------------------------------------------------ */
  class ReviewForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.status = this.querySelector('[data-review-status]');
      this.thanks = this.querySelector('[data-review-thanks]');
      if (!this.form) return;
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.decideVisibility();
    }

    /* Stamped renders late, so look again for a while before giving up. */
    decideVisibility() {
      let tries = 0;
      const look = () => {
        if (document.querySelector('#stamped-main-widget .new-review-form')) {
          this.hidden = true;
          return;
        }
        this.hidden = false;
        if (++tries < 20) setTimeout(look, 300);
      };
      look();
    }

    /* Stamped's own script publishes the key it fetched; ask for one only if
       it has not run. */
    async credentials() {
      const live = window.StampedGlobalOptions;
      if (live && live.apiKey) return live;
      if (this.keyPromise) return this.keyPromise;
      const shop = this.dataset.shop;
      this.keyPromise = fetch(`https://stamped.io/api/getappkey?shopShopifyDomain=${encodeURIComponent(shop)}`)
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);
      return this.keyPromise;
    }

    async onSubmit(event) {
      event.preventDefault();
      const data = new FormData(this.form);
      const rating = data.get('reviewRating');
      if (!rating) return this.fail(strings.reviewRating);
      if (!data.get('author') || !data.get('email') || !String(data.get('reviewMessage')).trim()) {
        return this.fail(strings.reviewRequired);
      }

      const creds = await this.credentials();
      if (!creds || !creds.apiKey) return this.fail(strings.reviewError);

      const d = this.dataset;
      data.append('productId', d.productId);
      data.append('productName', d.productName || '');
      data.append('productSKU', d.productSku || '');
      data.append('productType', d.productType || '');
      data.append('productUrl', d.productUrl || '');
      data.append('productImageUrl', d.productImage || '');
      data.append('reviewSource', 'widget');

      const query = new URLSearchParams({ apiKey: creds.apiKey, storeUrl: d.shop });
      if (creds.sId) query.set('sId', creds.sId);
      if (window.theme && window.theme.locale) query.set('locale', window.theme.locale);

      const button = this.form.querySelector('[type="submit"]');
      if (button) button.disabled = true;
      this.say(strings.reviewSending);

      try {
        const response = await fetch(`https://stamped.io/api/reviews3?${query}`, { method: 'POST', body: data });
        if (!response.ok) throw new Error(response.status);
        this.form.hidden = true;
        if (this.thanks) this.thanks.hidden = false;
        this.say('');
      } catch (error) {
        if (button) button.disabled = false;
        this.fail(strings.reviewError);
      }
    }

    say(message) {
      if (this.status) this.status.textContent = message || '';
    }

    fail(message) {
      this.say(message);
      if (this.status) this.status.classList.add('review-form__status--error');
    }
  }
  customElements.define('review-form', ReviewForm);

  /* The review marquee scrolls on its own, so WCAG asks for a way to stop it.
     Hover and focus-within are not enough: the cards hold no focusable
     element, and neither reaches a touch user. */
  class ReviewMarquee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-marquee-track]');
      this.toggle = this.querySelector('[data-marquee-toggle]');
      if (!this.track || !this.toggle) return;

      this.paused = false;
      this.toggle.hidden = false;
      this.toggle.addEventListener('click', () => {
        this.paused = !this.paused;
        this.track.classList.toggle('is-paused', this.paused);
        this.toggle.setAttribute('aria-pressed', String(this.paused));
        this.toggle.setAttribute(
          'aria-label',
          this.paused ? this.dataset.labelPlay || 'Play reviews' : this.dataset.labelPause || 'Pause reviews'
        );
        const pauseIcon = this.toggle.querySelector('[data-toggle-icon-pause]');
        const playIcon = this.toggle.querySelector('[data-toggle-icon-play]');
        if (pauseIcon) pauseIcon.hidden = this.paused;
        if (playIcon) playIcon.hidden = !this.paused;
      });
    }
  }
  customElements.define('review-marquee', ReviewMarquee);

  syncSaved();
  prefillContact();
})();
